import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import FolderIcon from '@mui/icons-material/Folder';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import {SimpleTreeView, TreeItem, TreeViewBaseItem} from '@mui/x-tree-view';
import {Box, Chip, Typography} from '@mui/material';
import {t} from 'i18next';
import './ArrTreeView.scss';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {ReactComponent as ScopeIcon} from '../../../assets/images/scope_icon.svg';
import {ReactComponent as TreeIcon} from '../../../assets/images/loads-not-indexed.svg';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import {ReactComponent as MoreIcon} from '../../../assets/images/icon-more.svg';
import ArrMenu, {ArrMenuHandle, CustomMenuItem} from '../arr-menu/ArrMenu';
import ArrButton from '../arr-button/ArrButton';
import ArrCheckbox from '../arr-checkbox/ArrCheckbox';
import {ReactComponent as TagTreeIcon} from '../../../assets/images/tag-tree.svg';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import ArrTooltipWrapper from '../arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as TickIcon} from '../../../assets/images/tick-icon.svg';
import {ReactComponent as FolderGroup} from '../../../assets/images/folder-group.svg';
import {ReactComponent as TagGroup} from '../../../assets/images/tag_group.svg';
import ArrTooltip from '../arr-tooltip/ArrTooltip';
import {
  AnimateLayoutChanges,
  defaultAnimateLayoutChanges,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {CSS} from '@dnd-kit/utilities';
import type {Modifier} from '@dnd-kit/core';
import {useCreateNode} from '../../../hooks/saved-search/savedSearch';

const cursorTopLeftModifier: Modifier = ({transform, activeNodeRect}) => {
  if (!activeNodeRect) return transform;

  return {
    ...transform,
    x: transform.x - activeNodeRect.width / 4,
    y: transform.y - activeNodeRect.height / 4,
  };
};

const getIconFromFileType = (fileType: string) => {
  switch (fileType) {
    case 'folder':
      return <FolderIcon className="location-folder-icon" />;
    case 'search':
      return <SearchOutlinedIcon className="location-folder-icon" />;
    case 'search_private':
      return <PersonSearchOutlinedIcon className="location-folder-icon" />;
    case 'loads_not_indexed':
      return <TreeIcon className="location-tree-icon" />;
    case 'tag':
      return <TagTreeIcon className="location-folder-icon" />;
    case 'tick':
      return <TickIcon className="location-tick-icon" />;
    case 'folder_group':
      return <FolderGroup className="location-tick-icon" />;
    case 'tag_group':
      return <TagGroup className="location-tick-icon" />;
  }
};

export type TrackItemsType = {
  label: string;
  id: string;
};

interface IArrTreeViewProps {
  itemsList: TreeViewBaseItem<ExtendedTreeItemProps>[];
  setSelectedItem?: (value: string[] | null, isCtrlPressed?: boolean) => void;
  selectedItem?: string | null;
  treeSelectedItems?: string[] | null;
  multipleSelection?: boolean;
  isShowCountLabel?: boolean;
  handleNoResultMessage?: string;
  allowGlobalSelect?: boolean;
  defaultExpandedIds?: string[];
  setExpandedFolders?: (data: string[]) => void;
  showActions?: boolean;
  showCheckmark?: boolean;
  selectedNodeToMove?: string;
  updateTreeData?: React.Dispatch<
    React.SetStateAction<TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined>
  >;
  isShowCheckbox?: boolean;
  isShowFileType?: boolean;
  MoreMenuOptions?: CustomMenuItem[];
  onMenuItemClick?: (item: CustomMenuItem) => void;
  customContentClass?: string;
  customContentHeight?: number;
  searchKeyword?: string;
  includeDisabledInSelectionState?: boolean;
  onRenameNode?: (
    text: string,
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => void;
  menuDirection?: 'left' | 'right' | 'top' | 'bottom';
  menuClassName?: string;
  leafTickExtraPadding?: boolean;
  showTooltip?: boolean;
  isChildrenIndendation?: boolean;
  isSingleSelectionOnCtrl?: boolean;
  enableDragAndDrop?: boolean;
  afterDragEnd?: (
    sourceId: TreeViewBaseItem<ExtendedTreeItemProps>,
    targetParentId: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => void;
  showButtonLoader?: boolean;
  onButtonClick?: (node: TreeViewBaseItem<ExtendedTreeItemProps>) => void;
  expandOnSearch?: boolean;
  checkDepth?: (level: number) => void;
  isBlockParentNodeSelection?: boolean;
  expandSelect?: boolean;
  isMultiItemsDragable?: boolean;
  nonMovableItems?: Array<string>;
  checkboxGroupSelection?: boolean;
  /** When true, folders (newFolders then folders) are rendered before files; default false */
  groupFoldersFirst?: boolean;
  parentIndependentSelection?: boolean;
  readActiveCheckboxAccessibilityProps?: boolean;
  buttonTestId?: string;
  dataTestId?: string;
  stopActionButtonClickPropagation?: boolean;
  stopActionButtonKeyPropagation?: boolean;
  expandOnClickForNonCheckbox?: boolean;
}

interface TreeItem {
  id: string;
  label: string;
  fileType: string;
  color: string;
  children?: TreeItem[];
}

const ArrTreeView = (props: IArrTreeViewProps) => {
  const {
    itemsList,
    setSelectedItem,
    selectedItem,
    treeSelectedItems,
    multipleSelection,
    isShowCountLabel = true,
    handleNoResultMessage, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    allowGlobalSelect = false,
    defaultExpandedIds = [''],
    setExpandedFolders,
    showActions = false,
    showCheckmark = false,
    selectedNodeToMove,
    updateTreeData,
    isShowCheckbox = false,
    MoreMenuOptions = [], // default to [] to satisfy ArrMenu items: CustomMenuItem[]
    onMenuItemClick,
    customContentHeight,
    customContentClass = '',
    includeDisabledInSelectionState = false,
    searchKeyword = '',
    isShowFileType = true,
    onRenameNode,
    menuDirection = 'left',
    menuClassName,
    leafTickExtraPadding = false,
    showTooltip,
    isChildrenIndendation = false,
    isSingleSelectionOnCtrl = false,
    enableDragAndDrop = false,
    afterDragEnd,
    onButtonClick,
    showButtonLoader = false,
    expandOnSearch = false,
    checkDepth,
    isBlockParentNodeSelection = false,
    expandSelect = true,
    isMultiItemsDragable = false,
    nonMovableItems = [],
    checkboxGroupSelection = true,
    groupFoldersFirst = false,
    parentIndependentSelection = false,
    readActiveCheckboxAccessibilityProps,
    buttonTestId,
    dataTestId = 'tree-view',
    stopActionButtonClickPropagation = false,
    stopActionButtonKeyPropagation = false,
    expandOnClickForNonCheckbox = false,
  } = props;

  const [selectedItems, setSelectedItems] = useState<
    string | string[] | null | undefined
  >(selectedItem);
  const inputRef = useRef<HTMLInputElement>(null);
  const [treeData, setTreeData] = useState(itemsList);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null); // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [nodeLimitLevel, setNodeLimitLevel] = useState<number>(0);
  const [renamingNodeId, setRenamingNodeId] = useState<string | null>(null);
  const [renameInputValue, setRenameInputValue] = useState<string>('');
  const [expandedIds, setExpandedIds] = useState<string[]>(defaultExpandedIds);

  // --- Search specific states ---
  const [filteredTreeData, setFilteredTreeData] = useState(treeData);
  const [matchedNodeIds, setMatchedNodeIds] = useState<string[]>([]);
  const menuRef1 = useRef<ArrMenuHandle | null>(null);
  const previousItemsListLengthRef = useRef<number>(itemsList.length);

  const [overId, setOverId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const itemsSignatureRef = useRef<string>('');
  const nextIdRef = useRef<number>(0);

  const {mutate: mutateCreateNode} = useCreateNode();
  const treeRef = useRef<HTMLDivElement | null>(null);
  const getDraggedNodes = (): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (isMultiItemsDragable && Array.isArray(selectedItems)) {
      return selectedItems
        .map((id) => findNodeById(treeData, id))
        .filter(Boolean) as TreeViewBaseItem<ExtendedTreeItemProps>[];
    }
    if (activeId) {
      const singleNode = findNodeById(treeData, activeId);
      return singleNode ? [singleNode] : [];
    }
    return [];
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 15,
        delay: 400,
        tolerance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const buildItemsSignature = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined
  ): string => {
    if (!nodes?.length) return '';

    const parts: string[] = [];

    const walk = (list: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      for (const n of list) {
        parts.push(
          [
            String(n.id),
            String(n.label ?? ''),
            String(n.name ?? ''),
            String(n.value ?? ''),
            String((n as any).count ?? ''),
            String((n as any).disabled ?? false),
            String((n as any).fileType ?? ''),
            String(n.children?.length ?? 0),
            String((n as any).actionButtonName ?? ''),
            String((n as any).actionButtonLabel ?? ''),
            String((n as any).showActionButtonWithLabel ?? ''),
            String((n as any).showProgress ?? 'false'),
            String((n as any).progressPercentage ?? ''),
            String((n as any).buttonLabelTooltipTitle ?? ''),
            String((n as any).buttonLabelTooltipContent ?? ''),
          ].join('~')
        );

        if (n.children?.length) walk(n.children);
      }
    };

    walk(nodes);

    return parts.join('|');
  };

  const extractNumericId = (id: unknown): number | null => {
    if (typeof id === 'number' && Number.isFinite(id)) return id;

    if (typeof id === 'string') {
      const matches = id.match(/\d+/g);
      const lastMatch = matches?.pop();
      if (lastMatch) {
        return Number(lastMatch);
      }
    }

    return null;
  };

  const getMaxNumericId = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined
  ): number => {
    if (!nodes?.length) return 0;

    let max = 0;

    const walk = (list: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      for (const n of list) {
        const num = extractNumericId(n.id);

        if (num !== null && num > max) max = num;

        if (n.children?.length) walk(n.children);
      }
    };

    walk(nodes);

    return max;
  };

  const animateLayoutChanges: AnimateLayoutChanges = (args) =>
    defaultAnimateLayoutChanges({...args, wasDragging: true});

  // --- Update tree data prop if needed ---
  useEffect(() => {
    const maxId = getMaxNumericId(treeData);
    nextIdRef.current = Math.max(nextIdRef.current, maxId);
    if (updateTreeData) {
      updateTreeData(treeData);
    }
  }, [treeData]);

  // --- Sync treeData with itemsList ---
  useEffect(() => {
    const nextSig = buildItemsSignature(itemsList);

    if (itemsSignatureRef.current !== nextSig) {
      itemsSignatureRef.current = nextSig;

      setTreeData(itemsList);

      if (!searchKeyword) {
        setFilteredTreeData(itemsList);
        setMatchedNodeIds([]);
      }
    }

    const wasEmpty = previousItemsListLengthRef.current === 0;
    previousItemsListLengthRef.current = itemsList.length;

    if (itemsList.length === 1 && !defaultExpandedIds.length) {
      const id = itemsList.map((item) => item.id);
      setExpandedIds(id);
    }
    if (
      wasEmpty &&
      itemsList.length > 0 &&
      defaultExpandedIds &&
      defaultExpandedIds.length > 0
    ) {
      setExpandedIds(defaultExpandedIds);
    }
  }, [itemsList, defaultExpandedIds, searchKeyword]);

  // --- Sync selected items ---
  useEffect(() => {
    if (multipleSelection) {
      setSelectedItems(treeSelectedItems);
    } else {
      setSelectedItems(selectedItem);
    }
  }, [multipleSelection, selectedItem, treeSelectedItems]);

  useEffect(() => {
    setExpandedFolders?.(expandedIds);
  }, [expandedIds, setExpandedFolders]);

  const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRenameInputValue(event.currentTarget.value);
  };

  // --- Filter tree recursively for search ---
  const handleBlurOrEnter = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    setRenamingNodeId(null);
    setRenameInputValue('');
    onRenameNode?.(renameInputValue, node);
  };

  const filterTree = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    query: string
  ): {
    filtered: TreeViewBaseItem<ExtendedTreeItemProps>[];
    matches: string[];
    filteredIds: string[];
  } => {
    let matches: string[] = [];
    let filteredIds: string[] = [];
    const filtered = nodes
      .map((node) => {
        const childrenResult = node.children
          ? filterTree(node.children, query)
          : {filtered: [], matches: [], filteredIds: []};
        const isMatch = node.label.toLowerCase().includes(query.toLowerCase());
        const hasMatchingChildren = childrenResult.filtered.length > 0;
        if (isMatch) matches.push(node.id);
        matches = [...matches, ...childrenResult.matches];
        if (isMatch || hasMatchingChildren) {
          filteredIds.push(node.id);
        }
        filteredIds = [...filteredIds, ...childrenResult.filteredIds];
        return isMatch || hasMatchingChildren
          ? {
              ...node,
              children: childrenResult.filtered,
              hasChildren: hasMatchingChildren, // hide chevron if no matching children
            }
          : null;
      })
      .filter(Boolean) as TreeViewBaseItem<ExtendedTreeItemProps>[];
    return {filtered, matches, filteredIds};
  };

  // --- Apply search whenever query or treeData changes ---
  useEffect(() => {
    if (!searchKeyword) {
      setFilteredTreeData(treeData);
      setMatchedNodeIds([]);
      expandOnSearch && setExpandedIds(defaultExpandedIds || []);
      return;
    }
    const {filtered, matches, filteredIds} = filterTree(
      treeData,
      searchKeyword
    );
    setFilteredTreeData(filtered);
    setMatchedNodeIds(matches);
    expandOnSearch && setExpandedIds(filteredIds);
  }, [searchKeyword, treeData]);

  const isParentNode = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    return (
      node.isGroup || (Array.isArray(node.children) && node.children.length > 0)
    );
  };

  // --- Existing functions ---
  const handleSelectedItemsChange = (
    event: SyntheticEvent<Element, Event> | null,
    ids: string[] | string | null
  ) => {
    const target = event?.target as HTMLElement | null;

    //Ignore selection when clicking expand / collapse icon
    if (target?.closest('.MuiTreeItem-iconContainer') && !expandSelect) {
      return;
    }
    const isCtrlPressed = Boolean(
      !!event &&
        (('ctrlKey' in event && (event as any).ctrlKey) ||
          ('nativeEvent' in event &&
            'ctrlKey' in (event as any).nativeEvent &&
            (event!.nativeEvent as MouseEvent | KeyboardEvent).ctrlKey))
    );

    const clickedIds = Array.isArray(ids) ? ids : ids ? [ids] : [];
    const clickedId = clickedIds[0];
    if (!clickedId) return;

    const selectedNode = findNodeById(treeData, clickedId);
    if (!selectedNode || selectedNode.disabled) return;

    // block selection of parent nodes
    if (isBlockParentNodeSelection && isParentNode(selectedNode)) {
      return;
    }

    if (showCheckmark) {
      const parentFolderId = findParentById(treeData, selectedNodeToMove ?? '');
      if (
        clickedId === selectedNodeToMove ||
        clickedId === parentFolderId ||
        selectedNode.fileType !== 'folder'
      ) {
        return;
      }
    }

    const isValidId = (id: string) =>
      id && !id.includes('loading') && id !== selectedNodeToMove;
    if (!isValidId(clickedId)) return;
    // Fix: !isSingleSelectionOnCtrl is used to allow single selection on Ctrl key press for accessibility considerations
    if (isCtrlPressed && !isSingleSelectionOnCtrl) {
      let newSelection = Array.isArray(selectedItems)
        ? [...selectedItems]
        : selectedItems
          ? [selectedItems?.toString()]
          : [];
      if (newSelection.includes(clickedId)) {
        newSelection = newSelection.filter((id) => id !== clickedId);
      } else {
        newSelection.push(clickedId);
      }
      setSelectedItems(newSelection);
      if (setSelectedItem) setSelectedItem(newSelection, isCtrlPressed);
    } else if (multipleSelection) {
      let newSelection = Array.isArray(selectedItems) ? [...selectedItems] : [];
      if (newSelection.includes(clickedId)) {
        newSelection = newSelection.filter((id) => id !== clickedId);
      } else {
        newSelection.push(clickedId);
      }
      setSelectedItems(newSelection);
      if (setSelectedItem) setSelectedItem(newSelection);
    } else {
      setSelectedItems(clickedId);
      if (setSelectedItem) setSelectedItem([clickedId], isCtrlPressed);
    }
  };

  const isNodeSelected = (nodeId: string) => {
    if (Array.isArray(selectedItems)) {
      return selectedItems.includes(nodeId);
    }
    return selectedItems === nodeId;
  };

  const getSelectionState = (
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ): {
    checked: boolean;
    indeterminate: boolean;
  } => {
    if (parentIndependentSelection) {
      return {
        checked: isNodeSelected(node.id),
        indeterminate: false,
      };
    }
    if (!node.children || node.children.length === 0) {
      return {
        checked: isNodeSelected(node.id),
        indeterminate: false,
      };
    }

    if (isShowCheckbox && !multipleSelection) {
      return {
        checked: isNodeSelected(node.id),
        indeterminate: false,
      };
    }

    // For parent nodes with children, derive state from children's states
    // - checked: all selectable (enabled) children are fully checked
    // - indeterminate: some but not all selectable children are selected (checked or indeterminate)
    // - unchecked: no selectable children selected
    // When includeDisabledInSelectionState is true, we still only consider enabled children
    // for the parent's checked state, but we include disabled children in the visual state calculation
    // to show their selected state if they were previously selected
    const enabledChildren = node.children.filter((c) => !c.disabled);
    const disabledChildren = includeDisabledInSelectionState
      ? node.children.filter((c) => c.disabled)
      : [];

    // Only check enabled children for determining parent's checked/indeterminate state
    let anyIndeterminate = false;
    let allEnabledChildrenChecked = enabledChildren.length > 0; // if no enabled children, keep as false
    let anyEnabledChildSelected = false;

    for (const child of enabledChildren) {
      const childState = getSelectionState(child);
      if (childState.indeterminate) anyIndeterminate = true;
      if (!childState.checked) allEnabledChildrenChecked = false;
      if (childState.checked || childState.indeterminate)
        anyEnabledChildSelected = true;
    }

    // Check if any disabled children are selected (for visual consistency)
    // but don't let them affect the parent's checked state
    let anyDisabledChildSelected = false;
    if (includeDisabledInSelectionState) {
      for (const child of disabledChildren) {
        if (isNodeSelected(child.id)) {
          anyDisabledChildSelected = true;
          break;
        }
      }
    }

    let allDisabledChildrenSelected = false;

    if (includeDisabledInSelectionState && node.children.length > 0) {
      const allChildrenDisabled =
        disabledChildren.length === node.children.length;

      allDisabledChildrenSelected =
        allChildrenDisabled &&
        disabledChildren.every((child) => isNodeSelected(child.id));
    }

    // If all enabled children are checked, parent is checked
    if (allEnabledChildrenChecked && enabledChildren.length > 0) {
      return {checked: true, indeterminate: false};
    }
    if (allDisabledChildrenSelected) {
      return {checked: true, indeterminate: false};
    }
    // If some enabled children are selected or any disabled children are selected, parent is indeterminate
    if (
      anyIndeterminate ||
      anyEnabledChildSelected ||
      anyDisabledChildSelected
    ) {
      return {checked: false, indeterminate: true};
    }
    return {checked: false, indeterminate: false};
  };

  const getAllChildNodeIds = (
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ): string[] => {
    if (!node.children) return [];
    return node.children.reduce<string[]>((acc, child) => {
      // When includeDisabledInSelectionState is true, we still need to recurse into
      // disabled children to find enabled descendants, but we don't include the disabled child itself
      if (child.disabled) {
        // Skip the disabled child itself, but recurse into its children to find enabled descendants
        return [...acc, ...getAllChildNodeIds(child)];
      }
      // Include the enabled child and recurse into its children
      return [...acc, child.id, ...getAllChildNodeIds(child)];
    }, []);
  };

  const handleCheckboxSelection = (checked: boolean, nodeId: string) => {
    const node = findNodeById(treeData, nodeId);
    if (!node) return;

    if (!multipleSelection) {
      if ((isParentNode(node) || node.skipParent) && isBlockParentNodeSelection)
        return;
      let value: string[] = [];
      if (checked) {
        value = [nodeId];
      }

      setSelectedItems(value);
      setSelectedItem && setSelectedItem(value);

      return;
    }

    // If parentIndependentSelection is true, toggle only the clicked node
    if (parentIndependentSelection) {
      setSelectedItems((prev) => {
        const current = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];
        let newSelection: string[];
        if (checked) {
          newSelection = current.includes(nodeId)
            ? current
            : [...current, nodeId];
        } else {
          newSelection = current.filter((id) => id !== nodeId);
        }
        if (setSelectedItem) setSelectedItem(newSelection);
        return newSelection;
      });
      return;
    }

    // When includeDisabledInSelectionState is true, we only toggle enabled children
    // Disabled children remain selected/deselected as they are
    const childIds = getAllChildNodeIds(node);
    const nodeIdsToChange = [nodeId, ...childIds];

    // Get current selection state
    const currentState = getSelectionState(node);

    // Special handling for indeterminate state:
    // When indeterminate, checkbox shows as checked, but clicking should SELECT all enabled children
    // (not deselect, even though MUI sends checked=false because it's trying to uncheck)
    if (currentState.indeterminate) {
      // Always select all enabled children when clicking indeterminate checkbox
      setSelectedItems((prev) => {
        const current = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];
        const newSelection = Array.from(
          new Set([...current, ...nodeIdsToChange])
        );
        if (setSelectedItem) setSelectedItem(newSelection);
        return newSelection;
      });
      return;
    }

    // Normal toggle behavior for checked/unchecked states
    setSelectedItems((prev) => {
      const current = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];
      if (checked) {
        // Add enabled children and parent to selection
        // Disabled children remain selected (they're not in nodeIdsToChange)
        const newSelection = Array.from(
          new Set([...current, ...nodeIdsToChange])
        );
        if (setSelectedItem) setSelectedItem(newSelection);
        return newSelection;
      } else {
        // Remove only enabled children and parent from selection
        // Disabled children remain selected (they're not in nodeIdsToChange)
        const newSelection = current.filter(
          (id) => !nodeIdsToChange.includes(id)
        );
        if (setSelectedItem) setSelectedItem(newSelection);
        return newSelection;
      }
    });
  };

  const handleMoreIconClick = (e: React.MouseEvent<HTMLElement>) => {
    menuRef1.current?.open(e.currentTarget as HTMLElement);
  };

  const handleMenuItemClick = (item: CustomMenuItem) => {
    if (item.id === 'rename') {
      const selectedId = Array.isArray(selectedItems)
        ? selectedItems[0]
        : selectedItems;
      const node = findNodeById(treeData, selectedId ?? '');
      if (node) {
        setRenamingNodeId(node.id);
        setRenameInputValue(node.label);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    }

    if (item.id === 'add_group') {
      const level = getNodeLevel(treeData, String(selectedItem));
      if (level && level > 5) {
        onMenuItemClick?.({...item, id: 'add_group_level_error'});
        return;
      }
      const parentId = Array.isArray(selectedItems)
        ? selectedItems[0]
        : selectedItems;

      if (!parentId) return;

      const node = findNodeById(treeData, parentId ?? '');

      const payload = {
        searchGroupLabel: 'New node',
        searchGroupID: 0,
        parentSearchGroupID: parentId,
        savedSearchId: 0,
        searchGroupType: '',
        searchGroupSecurityGroupId: node?.securitygroupsearchid,
        toolTipRef: '',
      };

      mutateCreateNode(payload, {
        onSuccess: (data) => {
          let childNode = data?.treeStructureData?.find(
            (i) => i.id == String(data?.id)
          );

          const newChild = createNewGroupNode(parentId, childNode);

          setTreeData((prev) => {
            const updated = addChildToTree(prev, parentId, newChild);
            return updated;
          });

          setExpandedIds((prev) => Array.from(new Set([...prev, parentId])));
          setSelectedItems(newChild?.id);
          setSelectedItem?.([newChild?.id]);
          setRenamingNodeId(newChild?.id);
          setRenameInputValue(newChild?.label);

          setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(
              0,
              inputRef.current.value.length
            );
          }, 0);
        },
        onError: (error) => {
          console.log('error mutateCreateNode', error);
        },
      });
    }
    onMenuItemClick?.(item);
  };

  const handleItemKeyDown = (
    event: React.KeyboardEvent<HTMLLIElement>,
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.stopPropagation();
      if (isShowCheckbox) {
        // Prevent default scrolling for space
        if (event.key === ' ') {
          event.preventDefault();
        }
        const state = getSelectionState(node);
        const shouldCheck = !state.checked || state.indeterminate;
        if ((node?.isGroup && !checkboxGroupSelection) || node?.disabled)
          return;
        handleCheckboxSelection(shouldCheck, node.id);
      }
    }
  };

  const createNewGroupNode = (parentId: string, childNode: any) => {
    const nextId = Number(childNode.id);
    nextIdRef.current = nextId;
    const label = childNode.text;

    const id = String(nextId);

    return {
      id,
      label,
      parent: parentId,
      fileType: '',
      showMoreOptions: true,
      children: [],
      isGroup: false,
      isNewGroup: true,
    } as unknown as TreeViewBaseItem<ExtendedTreeItemProps>;
  };

  const addChildToTree = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    parentId: string,
    child: TreeViewBaseItem<ExtendedTreeItemProps>
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    return nodes.map((n) => {
      if (n.id === parentId) {
        const nextChildren = [...(n.children ?? []), child];

        return {
          ...n,
          children: nextChildren,
          isGroup: true,
        };
      }

      if (n.children?.length) {
        return {...n, children: addChildToTree(n.children, parentId, child)};
      }

      return n;
    });
  };

  const handleActionButtonWrapperClick = (e: React.MouseEvent<HTMLElement>) => {
    if (stopActionButtonClickPropagation) {
      e.stopPropagation();
    }
  };

  const handleActionButtonKeyDownCapture = (
    e: React.KeyboardEvent<HTMLElement>
  ) => {
    if (
      stopActionButtonKeyPropagation &&
      (e.key === 'Enter' || e.key === ' ')
    ) {
      e.stopPropagation();
    }
  };

  const handleTreeActionButtonClick = (
    e: React.MouseEvent<HTMLElement>,
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    if (stopActionButtonClickPropagation) {
      e.stopPropagation();
    }
    onButtonClick?.(node);
  };

  const renderTreeNode = (
    node: TreeViewBaseItem<ExtendedTreeItemProps>,
    isHovered: boolean
  ) => (
    <div
      className="location-treeitem-label"
      data-testid={`arr-tree-view-div-parent-wrapper-${dataTestId}-${node.id}`}
    >
      {readActiveCheckboxAccessibilityProps && (
        <span
          id={`tree-node-state-${node.id}`}
          className="sr-only"
          data-testid={`arr-tree-view-active-inactive-${dataTestId}-${node.id}`}
        >
          {node.disabled ? 'inactive' : 'active'}
        </span>
      )}
      <div
        className={`location-treeitem ${node.newFolder ? 'new-input' : ''} `}
        data-testid={`arr-tree-view-div-btn-location-${dataTestId}-${node.id}`}
      >
        {isShowCheckbox && !node.skipParent && (
          <div
            className="tree-checkbox"
            data-testid={`${node?.id}-tree-checkbox`}
            onClick={(e) => e.stopPropagation()}
          >
            <ArrCheckbox
              size="xs"
              id={node?.id}
              checked={
                (!parentIndependentSelection &&
                  getSelectionState(node).indeterminate) ||
                getSelectionState(node).checked
              }
              isIndeterminate={
                parentIndependentSelection
                  ? false
                  : getSelectionState(node).indeterminate
              }
              onChange={(value) => {
                if (readActiveCheckboxAccessibilityProps && node.disabled)
                  return;
                handleCheckboxSelection(value, node.id);
              }}
              onClick={(e) => e.stopPropagation()}
              disabled={!!node?.disabled}
              // Fix: aria-labelledby is used to link the checkbox with the label for accessibility
              // This is important for screen readers to understand the context of the checkbox
              // It uses the node's label ID to ensure the checkbox is associated with the correct label
              aria-labelledby={`tree-node-label-${node.id}`}
              tabIndex={-1}
              inputProps={{
                tabIndex: -1,
                'aria-hidden': true,
                input: {
                  'data-testid': `${dataTestId}-checkbox-input-${node.id}`,
                },
              }}
              disableRipple
              aria-label={`${dataTestId}-checkbox-${node.id}`}
            />
          </div>
        )}
        {isShowFileType && getIconFromFileType(node.fileType ?? '')}
        {node.isScopeSelected && (
          <ScopeIcon className={'location-folder-icon scope-icon'} />
        )}
        <Typography
          className="typo-text body3_size custom-text-style full-width-text"
          sx={{flex: 1}}
          component="span"
          data-testid={`arr-tree-view-name-rename-${dataTestId}-${node.id}`}
        >
          <div
            className={`${node?.disabled ? 'label-disable' : ''}`}
            data-testid={`arr-tree-view-div-button-${dataTestId}-${node.id}`}
          >
            <div
              className="label-container"
              data-testid={`arr-tree-view-div-input-wrapper-${dataTestId}-${node.id}`}
            >
              {renamingNodeId === node.id ? (
                <input
                  type="text"
                  ref={inputRef}
                  name={renamingNodeId}
                  value={renameInputValue ?? ''}
                  className={`typo-text body3_size edit-input`}
                  onChange={handleLabelChange}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={() => {
                    handleBlurOrEnter(node);
                  }}
                  onKeyDownCapture={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                      handleBlurOrEnter(node);
                    }
                  }}
                  maxLength={50}
                  data-testid={`arr-tree-view-input-${dataTestId}-${node.id}`}
                />
              ) : (
                <div
                  className="label-text-container"
                  data-testid={`arr-tree-view-div-link-${dataTestId}-${node.id}`}
                >
                  <Typography
                    className={`typo-text body3_size ${
                      node?.disabled && 'label-disable'
                    } ${node.isScopeSelected && 'scope-label'}`}
                    id={`tree-node-label-${node.id}`}
                    data-testid={`arr-tree-view-node-label-${dataTestId}-${node.id}`}
                  >
                    {node.label}
                  </Typography>
                  {node.tooltipTitle && (
                    <ArrTooltipWrapper
                      title={node.tooltipTitle}
                      trigger={
                        <InfoIcon
                          className="info-icon"
                          data-testid="toggle-info-tooltip"
                        />
                      }
                      dataTestId={`tooltip-wrapper-${dataTestId}-${node.id}`}
                    >
                      {node.tooltipContent}
                    </ArrTooltipWrapper>
                  )}
                </div>
              )}
              {node.count && node.fileType !== 'folder' && isShowCountLabel && (
                <span
                  className={node?.disabled ? 'label-disable' : 'count-active'}
                  data-testid={`arr-tree-view-node-count-${dataTestId}-${node.id}`}
                >{` (${node.count})`}</span>
              )}
            </div>
            {node?.notYetIndexed && (
              <div data-testid={`arr-tree-view-not-yet-indexed`}>
                <Typography
                  className="typo-text body3_size count-active"
                  data-testid={`arr-tree-view-not-yet-indexed-text-${dataTestId}-${node.id}`}
                >
                  {node?.notYetIndexed} {t('common.treeView.notYetIndexed')}
                </Typography>
              </div>
            )}
          </div>
        </Typography>
      </div>
      <div
        className="tree-action-container"
        data-testid={`arr-tree-view-div-input-actions-wrapper-${dataTestId}-${node.id}`}
      >
        {showActions && node?.showActionButtonWithLabel ? (
          <div
            className="tree-button-container"
            data-testid={`arr-tree-view-div-text`}
          >
            <Typography
              className="typo-text body3_size"
              data-testid={`arr-tree-view-action-button-label-${dataTestId}-${node.id}`}
            >
              {node?.actionButtonLabel}
            </Typography>
            {node.buttonLabelTooltipTitle && (
              <ArrTooltipWrapper
                title={node.buttonLabelTooltipTitle}
                trigger={
                  <InfoIcon
                    className="info-icon"
                    data-testid="toggle-info-tooltip"
                  />
                }
                dataTestId={`tooltip-wrapper-${dataTestId}-${node.id}-action`}
              >
                {node.buttonLabelTooltipContent}
              </ArrTooltipWrapper>
            )}
            {node?.actionButtonName && (
              <div
                onClick={handleActionButtonWrapperClick}
                onKeyDownCapture={handleActionButtonKeyDownCapture}
              >
                <ArrButton
                  size="small"
                  variant="primary"
                  loading={isNodeSelected(node.id) ? showButtonLoader : false}
                  disabled={isNodeSelected(node.id) ? showButtonLoader : false}
                  onClick={(e) => handleTreeActionButtonClick(e, node)}
                  dataTestId={`${buttonTestId ?? 'arr-tree-view-button'}-${node?.id}`}
                >
                  {node.actionButtonName}
                </ArrButton>
              </div>
            )}
          </div>
        ) : null}
        {showActions && node?.showProgress ? (
          <div
            className="project-bar-percentage"
            data-testid={`arr-tree-view-progress-wrapper-${dataTestId}-${node.id}`}
          >
            <div
              className="progress-bar-container"
              data-testid={`arr-tree-view-div-container`}
            >
              <div
                className="progress-bar-fill"
                style={{width: `${node.progressPercentage}%`}}
                data-testid={`arr-tree-view-div-percentage-${dataTestId}-${node.id}`}
              ></div>
            </div>
            <Typography
              className="typo-text body3_size"
              data-testid={`arr-tree-view-percentage-text-${dataTestId}-${node.id}`}
            >
              {node.progressPercentage}%
            </Typography>
          </div>
        ) : null}
        <div
          className="more-action-container"
          data-testid={`arr-tree-view-div-show-more-actions-${dataTestId}-${node.id}`}
        >
          {showActions &&
            node?.showMoreOptions &&
            (isHovered || isNodeSelected(node.id)) && (
              <div
                data-testid={`arr-tree-view-more-options-${dataTestId}-${node.id}`}
              >
                {!node?.isDefault && (
                  <>
                    <ArrIconButton
                      onClick={(e) => handleMoreIconClick(e)}
                      icon={<MoreIcon className="more-icon" />}
                      ariaLabel="more-options"
                      shape="none"
                      className="more-icon-button"
                      testId={`arr-tree-view-more-options-icon-butto-${dataTestId}-${node.id}`}
                    />
                  </>
                )}
              </div>
            )}
          {isNodeSelected(node.id) && (
            <ArrMenu
              id="more-menu"
              ref={menuRef1}
              items={MoreMenuOptions}
              onItemClick={(item) => handleMenuItemClick(item)}
              direction={menuDirection}
              className={menuClassName}
              variant="condensed"
            />
          )}
        </div>
      </div>
    </div>
  );
  const renderTree = useCallback(
    (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[], depth = 0) => {
      // If groupFoldersFirst is true, render newFolders -> folders -> files.
      // Otherwise preserve the input order in `nodes`.
      const nodeArray = !groupFoldersFirst
        ? (() => {
            const newFolders = nodes.filter(
              (node) => node.fileType === 'folder' && node.newFolder
            );
            const folders = nodes.filter(
              (node) => node.fileType === 'folder' && !node.newFolder
            );
            const files = nodes.filter((node) => node.fileType !== 'folder');
            return [...newFolders, ...folders, ...files];
          })()
        : nodes;
      return nodeArray.map((node) => {
        const isLeaf = !node.children || node.children.length === 0;
        const hasTick = node.fileType === 'tick';
        const extraPadding = leafTickExtraPadding && isLeaf && hasTick ? 24 : 0;
        const isHovered = hoveredNode === node.id;
        const isMatched = matchedNodeIds.includes(node.id);
        const isSelected = getSelectionState(node);

        return (
          <TreeItem
            sx={{
              '& > .MuiTreeItem-content': {
                // base indentation + extra for tick leaf
                paddingLeft: `calc(8px + ${28 * depth}px + ${extraPadding}px + ${isLeaf && isChildrenIndendation ? 20 : 0}px)`,
              },
            }}
            slotProps={{
              root: {className: 'tooltip-padding'},
              iconContainer: {
                'data-testid': `${dataTestId}-expand-collapse-${node.id}`,
              },

              content: {
                className: `tree-item-content ${isSelected.checked && !isSelected.indeterminate ? 'tree-item-selected' : ''} ${customContentClass ?? ''} ${
                  node.added && 'tree-item-added'
                } ${isMatched ? 'highlight-search-node' : ''} ${node?.disabled && 'disabled'}`,
                style: {
                  height: customContentHeight ?? '',
                  color: node?.color ?? '',
                },
              },
            }}
            key={node.id}
            itemId={node.id}
            onMouseEnter={() => setHoveredNode(node.id)}
            onMouseLeave={() => setHoveredNode(null)}
            onKeyDown={(e: React.KeyboardEvent<HTMLLIElement>) =>
              handleItemKeyDown(e, node)
            }
            data-testid={`${node.id}-${dataTestId}-tree-item`}
            label={
              showTooltip ? (
                <ArrTooltip content={node.label} focusableWrapper>
                  {renderTreeNode(node, isHovered)}
                </ArrTooltip>
              ) : (
                renderTreeNode(node, isHovered)
              )
            }
          >
            {node.children && renderTree(node.children, depth + 1)}
            {!node.children && node.hasChildren && (
              <TreeItem
                itemId={`${node.id}-placeholder`}
                className="lazy-child"
                sx={{
                  paddingLeft: `calc(6px+var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth)})`,
                }}
              ></TreeItem>
            )}
          </TreeItem>
        );
      });
    },
    [
      hoveredNode,
      matchedNodeIds,
      leafTickExtraPadding,
      customContentClass,
      customContentHeight,
      showTooltip,
      selectedItems,
      treeData,
      isShowCheckbox,
      includeDisabledInSelectionState,
      isChildrenIndendation,
      renamingNodeId,
      renameInputValue,
      readActiveCheckboxAccessibilityProps,
    ]
  );

  const renderDraggableTree = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    depth = 0
  ) => {
    const nodeArray = !groupFoldersFirst
      ? (() => {
          const newFolders = nodes.filter(
            (node) => node.fileType === 'folder' && node.newFolder
          );
          const folders = nodes.filter(
            (node) => node.fileType === 'folder' && !node.newFolder
          );
          const files = nodes.filter((node) => node.fileType !== 'folder');
          return [...newFolders, ...folders, ...files];
        })()
      : nodes;

    return nodeArray.map((node) => {
      return <DraggableTreeItem key={node.id} node={node} depth={depth} />;
    });
  };

  const DraggableTreeItem: React.FC<{
    node: TreeViewBaseItem<ExtendedTreeItemProps>;
    depth: number;
  }> = ({node, depth}) => {
    const {
      setNodeRef,
      attributes,
      listeners,
      transform,
      transition,
      isDragging,
    } = useSortable({id: node.id, animateLayoutChanges});

    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const isLeaf = !node.children || node.children.length === 0;
    const hasTick = node.fileType === 'tick';
    const extraPadding = leafTickExtraPadding && isLeaf && hasTick ? 24 : 0;
    const isHovered = hoveredNode === node.id;
    const isMatched = matchedNodeIds.includes(node.id);
    const isOver = overId === node.id;

    return (
      <TreeItem
        sx={{
          '& > .MuiTreeItem-content': {
            paddingLeft: `calc(8px + ${24 * depth}px + ${extraPadding}px + ${isLeaf && isChildrenIndendation ? 20 : 0}px)`,
          },
        }}
        slotProps={{
          content: {
            ref: setNodeRef,
            ...attributes,
            ...listeners,
            className: `tree-item-content ${customContentClass ?? ''} ${
              node.added && 'tree-item-added'
            } ${isMatched ? 'highlight-search-node' : ''} ${node?.disabled && 'disabled'}  ${isOver && isLeaf && activeId != overId ? 'tree-item-background' : ''}
        ${!isLeaf && isOver ? 'tree-item-background' : ''}`,
            style: {
              ...style,
              height: customContentHeight ?? '',
              color: node?.color ?? '',
            },
          },
          iconContainer: {
            'data-testid': `${dataTestId}-expand-collapse-${node.id}`,
          },
          root: {
            ...listeners,
            tabIndex: -1,
            className: 'tooltip-padding',
          },
        }}
        key={node.id}
        itemId={node.id}
        data-testid={`${node.id}-${dataTestId}-draggable-item`}
        onMouseEnter={() => setHoveredNode(node.id)}
        onMouseLeave={() => setHoveredNode(null)}
        onKeyDown={(e: React.KeyboardEvent<HTMLLIElement>) =>
          handleItemKeyDown(e, node)
        }
        label={
          showTooltip ? (
            <ArrTooltip content={node.label} focusableWrapper>
              {renderTreeNode(node, isHovered)}
            </ArrTooltip>
          ) : (
            renderTreeNode(node, isHovered)
          )
        }
      >
        {node.children?.map((child) => (
          <DraggableTreeItem key={child.id} node={child} depth={depth + 1} />
        ))}
      </TreeItem>
    );
  };

  const getNodeLevel = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    nodeId: string,
    level = 1
  ): number | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return level;
      if (node.children) {
        const childLevel: number | null = getNodeLevel(
          node.children,
          nodeId,
          level + 1
        );
        if (childLevel) return childLevel;
      }
    }
    return null;
  };

  // Function to find a node by id
  const findNodeById = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    id: string
  ): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const findParentById = (
    treeData: TreeViewBaseItem<ExtendedTreeItemProps>[],
    nodeId: string
  ): string | null => {
    for (const node of treeData) {
      if (node.children?.some((child) => child.id === nodeId)) {
        return node.id;
      }
      if (node.children) {
        const parent = findParentById(node.children, nodeId);
        if (parent) return parent;
      }
    }
    return null;
  };

  const isDropAllowed = (
    activeId: string,
    overId: string | null,
    treeData: typeof filteredTreeData
  ) => {
    if (!overId) return false;
    if (activeId === overId) return false;

    const isDescendant = (
      parentId: string,
      childId: string,
      nodes: typeof treeData
    ): boolean => {
      const findNode = (
        id: string,
        list: typeof nodes
      ): (typeof nodes)[number] | null => {
        for (const n of list) {
          if (n.id === id) return n;
          if (n.children) {
            const found = findNode(id, n.children);
            if (found) return found;
          }
        }
        return null;
      };

      const parentNode = findNode(parentId, nodes);
      if (!parentNode || !parentNode.children) return false;

      const checkDescendant = (n: typeof parentNode): boolean => {
        if (!n.children) return false;
        for (const child of n.children) {
          if (child.id === childId) return true;
          if (checkDescendant(child)) return true;
        }
        return false;
      };

      return checkDescendant(parentNode);
    };

    return !isDescendant(activeId, overId, treeData);
  };

  const isDropAllowedMulti = (
    draggedNodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    overId: string | null
  ) => {
    if (!overId) return false;

    const hasNonMovableItem = draggedNodes.some((node) =>
      nonMovableItems.includes(node.id)
    );
    if (hasNonMovableItem) return false;

    return draggedNodes.every((node) =>
      isDropAllowed(node.id, overId, filteredTreeData)
    );
  };

  const onDragMove = ({over}: DragMoveEvent) => {
    setOverId(over?.id?.toString() ?? null);
  };

  const onDragEnd = ({over}: DragEndEvent) => {
    setActiveId(null);
    setOverId(null);
    if (!over) return;

    const overNodeId = over.id.toString();
    const draggedNodes = getDraggedNodes();

    // Prevent fallback when nothing to drag
    if (!draggedNodes.length) return;

    // Check multi or single drop validity
    const allowed = isDropAllowedMulti(draggedNodes, overNodeId);
    if (!allowed) return;

    const targetNode = findNodeById(treeData, overNodeId);
    if (!targetNode) return;

    if (!isMultiItemsDragable) {
      // single-item behavior (same as before)
      afterDragEnd?.(draggedNodes[0], targetNode);
    } else {
      // call once with a virtual group
      const virtualSource: TreeViewBaseItem<ExtendedTreeItemProps> = {
        id: '__multi_drag__',
        label: `${draggedNodes.length} items`,
        data: draggedNodes,
      };

      afterDragEnd?.(virtualSource, targetNode);
    }
  };

  useEffect(() => {
    const level = getNodeLevel(treeData, hoveredNode as string);
    if (level === 5) {
      setNodeLimitLevel(5);
    } else {
      setNodeLimitLevel(0);
    }
  }, [hoveredNode]);

  useEffect(() => {
    const level = getNodeLevel(treeData, String(selectedItem));
    checkDepth?.(level ?? 0);
  }, [selectedItems, selectedItem]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(0, inputRef.current.value.length);
    }
  }, [renamingNodeId]);

  //prevent scroll
  useEffect(() => {
    const container = treeRef.current;
    if (!container) return;

    const originalFocus = HTMLElement.prototype.focus;

    HTMLElement.prototype.focus = function (options?: FocusOptions) {
      if (container.contains(this) && this.closest('.MuiTreeItem-root')) {
        originalFocus.call(this, {
          ...(options || {}),
          preventScroll: true,
        });
        return;
      }

      originalFocus.call(this, options);
    };

    return () => {
      HTMLElement.prototype.focus = originalFocus;
    };
  }, []);
  return (
    <Box ref={treeRef} width="100%">
      {filteredTreeData && filteredTreeData.length > 0 ? (
        enableDragAndDrop ? (
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            modifiers={[cursorTopLeftModifier]}
            onDragStart={(event) => {
              const id = event.active.id.toString();
              setActiveId(id);
              if (
                multipleSelection &&
                Array.isArray(selectedItems) &&
                selectedItems.length > 1 &&
                !selectedItems.includes(id)
              ) {
                setSelectedItems(id);
              }
            }}
            onDragMove={onDragMove}
            onDragEnd={onDragEnd}
          >
            <SimpleTreeView
              className="trr-item-check"
              sx={{
                '& .MuiTreeItem-content[data-focused="true"]': {
                  backgroundColor: 'transparent !important',
                },
              }}
              selectedItems={selectedItems}
              multiSelect={multipleSelection}
              onSelectedItemsChange={
                !isShowCheckbox ? handleSelectedItemsChange : undefined
              }
              // itemChildrenIndentation={24} removing this as we are manually managing it
              expansionTrigger="iconContainer"
              onExpandedItemsChange={(event, ids) => {
                const isCtrlPressed =
                  !!event &&
                  ((event as React.SyntheticEvent & {ctrlKey?: boolean})
                    .ctrlKey ||
                    (
                      event as React.SyntheticEvent & {
                        nativeEvent?: MouseEvent | KeyboardEvent;
                      }
                    ).nativeEvent?.ctrlKey === true);
                if (multipleSelection && event) {
                  event.stopPropagation();
                }
                if (!isCtrlPressed) setExpandedIds(ids);
              }}
              expandedItems={expandedIds}
              onItemFocus={(
                event: React.SyntheticEvent | null,
                itemId: string
              ) => {
                setHoveredNode(itemId);
              }}
              onItemClick={(event: React.SyntheticEvent, itemId: string) => {
                // Enable row click to toggle checkbox selection while preserving existing behavior
                if (!isShowCheckbox) return;
                const target = event.target as HTMLElement | null;
                if (
                  target &&
                  (target.closest('.tree-checkbox') ||
                    target.closest('.more-action-container') ||
                    target.closest('.edit-input') ||
                    target.closest('.arr-button') ||
                    target.closest('button') ||
                    target.closest('[role="button"]') ||
                    target.closest('a'))
                ) {
                  return;
                }
                const node = findNodeById(treeData, itemId);
                if (!node || node.disabled) return;
                const state = getSelectionState(node);
                const shouldCheck = !state.checked || state.indeterminate;
                if (node?.isGroup && !checkboxGroupSelection) return;
                handleCheckboxSelection(shouldCheck, itemId);
              }}
            >
              {enableDragAndDrop
                ? renderDraggableTree(filteredTreeData)
                : renderTree(filteredTreeData)}
            </SimpleTreeView>

            <DragOverlay>
              {activeId && (
                <Chip
                  label={
                    <Typography
                      className="typo-text label3_size"
                      data-testid={`arr-tree-view-tactive-id-${dataTestId}`}
                    >
                      {isMultiItemsDragable &&
                      Array.isArray(selectedItems) &&
                      selectedItems.length > 1
                        ? `${selectedItems.length} ${t('common.nodes')}`
                        : t('common.node')}
                    </Typography>
                  }
                  className={`drag-overlay-tree ${
                    isDropAllowedMulti(getDraggedNodes(), overId)
                      ? 'drag-allowed'
                      : 'drag-not-allowed'
                  }`}
                />
              )}
            </DragOverlay>
          </DndContext>
        ) : (
          <SimpleTreeView
            className="trr-item-check"
            sx={{
              '& .MuiTreeItem-content[data-focused="true"]': {
                backgroundColor: 'transparent !important',
              },
            }}
            selectedItems={selectedItems}
            multiSelect={multipleSelection}
            onSelectedItemsChange={
              !isShowCheckbox ? handleSelectedItemsChange : undefined
            }
            // itemChildrenIndentation={24} removing this as we are manually managing it
            expansionTrigger="iconContainer"
            onExpandedItemsChange={(event, ids) => {
              const isCtrlPressed =
                !!event &&
                ((event as React.SyntheticEvent & {ctrlKey?: boolean})
                  .ctrlKey ||
                  (
                    event as React.SyntheticEvent & {
                      nativeEvent?: MouseEvent | KeyboardEvent;
                    }
                  ).nativeEvent?.ctrlKey === true);
              if (multipleSelection && event) {
                event.stopPropagation();
              }
              if (!isCtrlPressed) setExpandedIds(ids);
            }}
            expandedItems={expandedIds}
            onItemFocus={(
              event: React.SyntheticEvent | null,
              itemId: string
            ) => {
              setHoveredNode(itemId);
            }}
            onItemClick={(event: React.SyntheticEvent, itemId: string) => {
              // Enable row click to toggle checkbox selection while preserving existing behavior
              const target = event.target as HTMLElement | null;
              if (!isShowCheckbox) return;

              if (target) {
                // Do not select an item when you only click the expand/collapse icon
                const svgDataTestId = target.getAttribute('data-testid');
                const pathD = target.getAttribute('d');

                // Expand <SVG /> or internal <PATH /> element
                if (svgDataTestId === 'TreeViewExpandIconIcon') {
                  return;
                }
                if (
                  pathD === 'M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'
                ) {
                  return;
                }

                // Collapse <SVG /> or internal <PATH /> element
                if (svgDataTestId === 'TreeViewCollapseIconIcon') {
                  return;
                }
                if (pathD === 'M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z') {
                  return;
                }
              }

              if (
                target &&
                (target.closest('.tree-checkbox') ||
                  target.closest('.more-action-container') ||
                  target.closest('.edit-input') ||
                  target.closest('.arr-button') ||
                  target.closest('button') ||
                  target.closest('[role="button"]') ||
                  target.closest('a'))
              ) {
                return;
              }
              const node = findNodeById(treeData, itemId);
              if (!node || node.disabled) return;

              // Nodes WITHOUT checkbox → act like expand/collapse
              if (expandOnClickForNonCheckbox && node.skipParent) {
                setExpandedIds((prev) => {
                  if (prev.includes(itemId)) {
                    return prev.filter((id) => id !== itemId); // collapse
                  } else {
                    return [...prev, itemId]; // expand
                  }
                });
                return;
              }

              const state = getSelectionState(node);
              const shouldCheck = !state.checked || state.indeterminate;
              if (node?.isGroup && !checkboxGroupSelection) return;
              handleCheckboxSelection(shouldCheck, itemId);
            }}
          >
            {enableDragAndDrop
              ? renderDraggableTree(filteredTreeData)
              : renderTree(filteredTreeData)}
          </SimpleTreeView>
        )
      ) : (
        handleNoResultMessage && (
          <Typography
            className="typo-text body3_size custom-text"
            data-testid={`arr-tree-view-typography-no-result`}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {handleNoResultMessage}
          </Typography>
        )
      )}
    </Box>
  );
};

export default ArrTreeView;
